import {
  MutationOptions,
  QueryOptions,
  QueryResult,
  MutationResult,
  ScheduledEventDto,
  ScheduledEventsLog,
  ScheduledEventMutationPayload,
  ScheduledEventActivationPayload,
  ScheduledEventsExecutionInfoDto,
} from '@thingslog/repositories';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';
import { QueryKeys } from '../enums/QueryKeys';

export interface ScheduledEventsQueryClient {
  getAllScheduledEventsInCompany: (companyId: number) => Promise<ScheduledEventDto[]>;
  getScheduledEventsLogsForEvent: (scheduledEventId: number) => Promise<ScheduledEventsLog[]>;
  useAllScheduledEventsInCompany: (
    companyId: number,
    options?: QueryOptions<ScheduledEventDto[]>
  ) => QueryResult<ScheduledEventDto[]>;
  useScheduledEventsLogsForEvent: (
    scheduledEventId: number,
    options?: QueryOptions<ScheduledEventsLog[]>
  ) => QueryResult<ScheduledEventsLog[]>;
  addScheduledEvents: (
    body: ScheduledEventMutationPayload
  ) => Promise<ScheduledEventMutationPayload>;
  useAddScheduledEventsData: (
    options?: MutationOptions<ScheduledEventMutationPayload, ScheduledEventMutationPayload>
  ) => MutationResult<ScheduledEventMutationPayload, ScheduledEventMutationPayload>;
  usePatchScheduledEvent: (
    options?: MutationOptions<void, PatchMutationProps>
  ) => MutationResult<void, PatchMutationProps>;
  deleteScheduledEvent: (scheduledEventId: number) => Promise<void>;
  useDeleteScheduledEvent: (
    options?: MutationOptions<void, number>
  ) => MutationResult<void, number>;
  getSpecificScheduledEventById: (scheduledEventId: number) => Promise<ScheduledEventDto>;
  useGetSpecificScheduledEventById: (
    scheduledEventId: number,
    options?: QueryOptions<ScheduledEventDto>
  ) => QueryResult<ScheduledEventDto>;
  updateScheduledEvent: (
    scheduledEventId: number,
    body: ScheduledEventMutationPayload
  ) => Promise<void>;
  useUpdateScheduledEventData: (
    options?: MutationOptions<void, PutMutationProps>
  ) => MutationResult<void, PutMutationProps>;
  getScheduledEventLogDetail: (
    scheduledEventId: number,
    scheduledEventLogId: number
  ) => Promise<ScheduledEventsExecutionInfoDto>;

  useGetScheduledEventLogDetail: (
    scheduledEventId: number,
    scheduledEventLogId: number,
    options?: QueryOptions<ScheduledEventsExecutionInfoDto>
  ) => QueryResult<ScheduledEventsExecutionInfoDto>;
}

export function createScheduledEventsQueryClient(axios: AxiosInstance): ScheduledEventsQueryClient {
  return new ScheduledEventsQueryClientImp(axios);
}

class ScheduledEventsQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public getAllScheduledEventsInCompany = async (
    companyId: number
  ): Promise<ScheduledEventDto[]> => {
    return this.axios
      .get(`/api/scheduled-events`, { params: { companyId } })
      .then((response: AxiosResponse<ScheduledEventDto[]>) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  };

  public getScheduledEventsLogsForEvent = async (
    scheduledEventId: number
  ): Promise<ScheduledEventsLog[]> => {
    return this.axios
      .get(`/api/scheduled-events-logs`, { params: { scheduledEventId } })
      .then((response: AxiosResponse<ScheduledEventsLog[]>) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  };

  public addScheduledEvents = async (
    body: ScheduledEventMutationPayload
  ): Promise<ScheduledEventMutationPayload> => {
    return await this.axios.post(`/api/scheduled-events`, body).then((response: AxiosResponse) => {
      return response.data;
    });
  };

  public patchScheduledEvent = async (
    id: number,
    body: ScheduledEventActivationPayload
  ): Promise<void> => {
    return await this.axios
      .patch(`/api/scheduled-events/${id}`, body, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response: AxiosResponse) => response.data);
  };

  public deleteScheduledEvent = async (scheduledEventId: number): Promise<void> => {
    return await this.axios
      .delete(`/api/scheduled-events/${scheduledEventId}`)
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  public getSpecificScheduledEventById = async (
    scheduledEventId: number
  ): Promise<ScheduledEventDto> => {
    return await this.axios
      .get(`/api/scheduled-events/${scheduledEventId}`)
      .then((response: AxiosResponse) => response.data);
  };

  public updateScheduledEvent = async (
    scheduledEventId: number,
    body: ScheduledEventMutationPayload
  ): Promise<void> => {
    return await this.axios
      .put(`/api/scheduled-events/${scheduledEventId}`, body)
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  public useAllScheduledEventsInCompany = (
    companyId: number,
    options?: QueryOptions<ScheduledEventDto[]>
  ): QueryResult<ScheduledEventDto[]> => {
    return useQuery<ScheduledEventDto[], AxiosError>(
      [QueryKeys.GetAllScheduledEventsInCompany, companyId],
      () => this.getAllScheduledEventsInCompany(companyId),
      options
    );
  };

  public useScheduledEventsLogsForEvent = (
    scheduledEventId: number,
    options?: QueryOptions<ScheduledEventsLog[]>
  ): QueryResult<ScheduledEventsLog[]> => {
    return useQuery<ScheduledEventsLog[], AxiosError>(
      [QueryKeys.GetScheduledEventsLogsForEvent, scheduledEventId],
      () => this.getScheduledEventsLogsForEvent(scheduledEventId),
      options
    );
  };

  public useAddScheduledEventsData = (
    options?: MutationOptions<ScheduledEventMutationPayload, ScheduledEventMutationPayload>
  ): MutationResult<ScheduledEventMutationPayload, ScheduledEventMutationPayload> => {
    return useMutation<ScheduledEventMutationPayload, AxiosError, ScheduledEventMutationPayload>(
      [QueryKeys.AddValueRange],
      (variables: ScheduledEventMutationPayload) => this.addScheduledEvents(variables),
      options
    );
  };

  public usePatchScheduledEvent = (
    options?: MutationOptions<void, PatchMutationProps>
  ): MutationResult<void, PatchMutationProps> => {
    return useMutation<void, AxiosError, PatchMutationProps>(
      [QueryKeys.PatchScheduledEvent],
      (props: PatchMutationProps) => this.patchScheduledEvent(props.id, props.body),
      options
    );
  };

  public useDeleteScheduledEvent = (
    options?: MutationOptions<void, number>
  ): MutationResult<void, number> => {
    return useMutation<void, AxiosError, number>(
      [QueryKeys.DeleteScheduledEvent],
      (scheduledEventId: number) => this.deleteScheduledEvent(scheduledEventId),
      options
    );
  };

  public useGetSpecificScheduledEventById = (
    scheduledEventId: number,
    options?: QueryOptions<ScheduledEventDto>
  ): QueryResult<ScheduledEventDto> => {
    return useQuery<ScheduledEventDto, AxiosError>(
      [QueryKeys.GetSpecificScheduledEventById, scheduledEventId],
      () => this.getSpecificScheduledEventById(scheduledEventId),
      options
    );
  };

  public useUpdateScheduledEventData = (
    options?: MutationOptions<void, PutMutationProps>
  ): MutationResult<void, PutMutationProps> => {
    return useMutation<void, AxiosError, PutMutationProps>(
      [QueryKeys.UpdateValueRange],
      (props: PutMutationProps) => this.updateScheduledEvent(props.id, props.body),
      options
    );
  };

  public getScheduledEventLogDetail = async (
    scheduledEventId: number,
    scheduledEventLogId: number
  ): Promise<ScheduledEventsExecutionInfoDto> => {
    return this.axios
      .get(`/api/scheduled-events/${scheduledEventId}/logs/${scheduledEventLogId}`)
      .then((response: AxiosResponse<ScheduledEventsExecutionInfoDto>) => {
        return response.data;
      });
  };

  public useGetScheduledEventLogDetail = (
    scheduledEventId: number,
    scheduledEventLogId: number,
    options?: QueryOptions<ScheduledEventsExecutionInfoDto>
  ): QueryResult<ScheduledEventsExecutionInfoDto> => {
    return useQuery<ScheduledEventsExecutionInfoDto, AxiosError>(
      [QueryKeys.GetScheduledEventTaskExecutions, scheduledEventId, scheduledEventLogId],
      () => this.getScheduledEventLogDetail(scheduledEventId, scheduledEventLogId),
      options
    );
  };
}

interface PatchMutationProps {
  id: number;
  body: ScheduledEventActivationPayload;
}

interface PutMutationProps {
  id: number;
  body: ScheduledEventMutationPayload;
}
